import React from 'react';
import { Button as RebassButton } from '@dutchie/common/components/rebass-compat';
import { layout, space, fontSize, maxWidth } from 'styled-system';
import styled from 'styled-components';
import _ from 'lodash';
import { SmallLoader } from 'shared/components/loading';
import useStyledTheme from 'shared/hooks/use-styled-theme';

const LoaderContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const StyledButton = styled(({ disabled, ...props }) => (
  <RebassButton disabled={disabled} {..._.omit(props, 'loading')} />
))`
  background-color: var(--dct--customized-colors-buttonsLinks, ${({ theme }) => theme?.customized?.colors?.buttonsLinks ?? theme.colors.primaryBlue});
  border-radius: var(--dct--customized-radius-buttons, ${({ theme }) => theme?.customized?.radius?.buttons ?? '33px'});
  border: none;
  box-shadow: none;
  color: ${(props) => (props.loading ? 'transparent !important' : '#FFF')};
  cursor: ${(props) => (props.loading || props.disabled ? 'not-allowed' : 'pointer')};
  display: block;
  font-family: var(--dct--customized-fonts-primary, ${({ theme }) => theme?.customized?.fonts?.primary});
  font-size: 14px;
  font-weight: bold;
  height: ${(props) => (props.height ? props.height : '40px')};
  line-height: 1;
  padding: 0 18px;
  position: relative;
  text-align: center;
  text-transform: var(--dct--customized-textTransforms-buttons, ${({ theme }) => theme?.customized?.textTransforms?.buttons ?? 'uppercase'});
  transition: background-color 86ms ease-out;
  white-space: nowrap;

  ${(props) => {
    if (props.height === '33px') {
      return `line-height: 2.4;`;
    }
    if (props.height === '27px') {
      return `line-height: 2;`;
    }
    return `line-height: 2.5;`;
  }}

  ${layout}
  ${space}
  ${fontSize}
  ${maxWidth}
  &:hover, &:active, &:focus {
    background-color: var(--dct--customized-colors-buttonsLinks, ${({ theme }) => theme?.customized?.colors?.buttonsLinks ?? theme.colors.primaryBlue});
    background-image: linear-gradient(rgba(0, 0, 0, 0.15) 0 0);
    border: none;
    box-shadow: none;
    color: #fff;
    outline: none;
  }

  &:focus:not(:hover) {
    background-color: var(--dct--customized-colors-buttonsLinks, ${({ theme }) => theme?.customized?.colors?.buttonsLinks ?? theme.colors.primaryBlue});
    background-image: none;
  }

  &.--is-disabled,
  &:disabled,
  &:disabled:hover {
    background-color: #c6c4c4;
  }

  ${(props) => {
    if (props.disabled) {
      return `
        background-color: #9ea7ab;
        color: #ffffff;
        opacity: 1;
      `;
    }
    if (props.disabled || props.loading) {
      return `
        &:hover, &:active, &:focus {
          background-color: #9ea7ab;
          color: #ffffff;
        }
      `;
    }
    return '';
  }}

  ${(props) =>
    props.inverted &&
    `
    background-color: ${props.backgroundColor || 'white'};
    background-image: none;
    border: 2px solid var(--dct--customized-colors-buttonsLinks, ${props.theme?.customized?.colors?.buttonsLinks ?? props.theme.colors.primaryBlue});
    color: ${
      props.loading ? 'transparent' : `var(--dct--customized-colors-buttonsLinks, ${props.theme?.customized?.colors?.buttonsLinks ?? props.theme.colors.primaryBlue})`
    };
    &:hover, &:active, &:focus {
      background-color: #f2f2f2;
      background-image: none;
      border: 2px solid var(--dct--customized-colors-buttonsLinks, ${props.theme?.customized?.colors?.buttonsLinks ?? props.theme.colors.primaryBlue});
      color: var(--dct--customized-colors-buttonsLinks, ${props.theme?.customized?.colors?.buttonsLinks ?? props.theme.colors.primaryBlue});
    }
    &:focus:not(:hover) {
      background-color: #f2f2f2;
      background-image: none;
    }
  `}

  ${(props) =>
    props.gray &&
    `
    background-color: #a3b2c1;
    color: ${props.loading ? 'transparent' : '#FFF'};
    &:hover, &:active {
      background-color: #777f85;
    }
    &:focus:not(:hover),
    &:disabled:hover {
      background-color: #a3b2c1;
    }
    opacity: ${props.disabled ? '0.5' : '1'};
  `}

  ${(props) =>
    props.link &&
    `
    background-color: #fff;
    background-image: none;
    color: ${
      props.loading ? 'transparent' : `var(--dct--customized-colors-buttonsLinks, ${props.theme?.customized?.colors?.buttonsLinks ?? props.theme.colors.primaryBlue})`
    };
    font-weight: normal;
    text-transform: none;
    &:hover, &:active {
      color: var(--dct--customized-colors-buttonsLinks, ${props.theme?.customized?.colors?.buttonsLinks ?? props.theme.colors.primaryBlue});
      background-color: #fff;
      background-image: none;
    }
    &:focus {
      background-color: #fff;
      background-image: none;
    }
  `}
`;

StyledButton.defaultProps = {
  theme: {
    colors: {
      primaryBlue: '#4597e0',
    },
  },
};

const Button = ({
  loading = false,
  disabled = false,
  children,
  inverted = false,
  link = false,
  onClick = _.noop,
  ...props
}) => {
  const theme = useStyledTheme();
  const spinnerColor = inverted ? theme?.customized?.colors?.buttonsLinks ?? theme.colors.primaryBlue : '#fff';
  return (
    <StyledButton
      loading={loading}
      disabled={disabled || loading}
      inverted={inverted}
      link={link}
      onClick={disabled || loading ? _.noop : onClick}
      {...props}
    >
      {children}
      {loading && (
        <LoaderContainer>
          <SmallLoader aria-label='Loading' height={20} color={spinnerColor} />
        </LoaderContainer>
      )}
    </StyledButton>
  );
};
export default Button;
